import React from 'react';
import { Grid, Dialog, DialogContent, Divider, Typography, Paper, CardMedia, IconButton, Box, styled } from '@mui/material';
import { grey } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
// API
import { Card, CardFaces, CardFace } from 'api/card';
// components
import { SymbolTypography } from 'components/ManaSymbol';
// theme
import { theme } from 'theme';

const TextBox = styled(Box)({
  [theme.breakpoints.up('sm')]: {
    minHeight: 96
  },
});

type Props = {
  card: Card | undefined,
  open: boolean,
  onClose: () => void,
};

const CardDetail: React.FC<Props> = ({ card, open, onClose }) => {
  const c = card ?? defaultCard;
  const imageUrl: string = c.image_url_large;

  const renderNormalCardText = (
    <Paper variant='outlined' sx={{ padding: 1, m: 0 }}>
      <Typography variant='h6'>
        {`${c.ja_name} / ${c.original_name}`}
      </Typography>
      <Typography variant='caption'>
        {`(${c.name_furigana})`}
      </Typography>
      <Divider sx={{ marginY: 1 }} />
      <SymbolTypography text={c.mana_cost} />
      <Divider sx={{ marginY: 1 }} />
      <Typography>{c.ja_type_line}</Typography>
      <Divider sx={{ marginY: 1 }} />
      <TextBox>
        <SymbolTypography text={c.ja_text} />
      </TextBox>
      <Divider sx={{ marginY: 1 }} />
      <TextBox>
        <SymbolTypography text={c.oracle_text} />
      </TextBox>
      <Divider sx={{ marginY: 1 }} />
      <Typography>
        {c.power !== '' ? `${c.power}/${c.toughness}` : ''}
      </Typography>
      <Divider sx={{ marginY: 1 }} />
      <Typography variant='caption'>
        {`初印刷：${c.first_printing}`}
        <br />
        {`モダン入り：${c.first_modern_printing}`}
      </Typography>
    </Paper>
  );

  const SplitCardText: React.FC<{ faces: CardFaces }> = ({ faces }) => {
    const f1 = faces.face1;
    const f2 = faces.face2;

    const renderCardFace = (face: CardFace) => (
      <Box>
        <Typography variant='h6'>
          {`${face.ja_name} / ${face.original_name}`}
        </Typography>
        <Typography variant='caption'>
          {`(${face.name_furigana})`}
        </Typography>
        <Divider sx={{ marginY: 1 }} />
        <SymbolTypography text={face.mana_cost} />
        <Divider sx={{ marginY: 1 }} />
        <Typography>{face.ja_type_line}</Typography>
        <Divider sx={{ marginY: 1 }} />
        <SymbolTypography text={face.ja_text} />
        <Divider sx={{ marginY: 1 }} />
        <SymbolTypography text={face.oracle_text} />
        <Divider sx={{ marginY: 1 }} />
      </Box>
    );
    return (
      <Paper variant='outlined' sx={{ padding: 1, m: 0 }
      }>
        {renderCardFace(f1)}
        < Divider sx={{ marginY: 1 }
        } />
        {renderCardFace(f2)}
        <Typography variant='caption'>
          {`初印刷：${c.first_printing}`}
          <br />
          {`モダン入り：${c.first_modern_printing}`}
        </Typography>
      </Paper >
    );
  };

  const renderCardText = () => {
    switch (c.layout) {
      case 'normal':
        return renderNormalCardText;
      case 'split':
        const defaultCardFaces: CardFaces = { 'face1': defaultCardFace, 'face2': defaultCardFace }
        return <SplitCardText faces={c.card_faces ?? defaultCardFaces} />
      default:
        return renderNormalCardText;
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          overflowY: 'unset',
        }
      }}>
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: -20,
          top: -20,
          color: 'gray',
          backgroundColor: grey[400],
          "&:hover": {
            backgroundColor: grey[500],
          }
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <CardMedia component='img' src={imageUrl} />
          </Grid>
          <Grid item xs sm>
            {renderCardText()}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CardDetail;

const defaultCard: Card = {
  id: 0,
  multiverse_id: 0,
  original_name: 'q',
  ja_name: 'q',
  name_furigana: 'q',
  mana_cost: 'q',
  cmc: 0,
  type_line: 'q',
  supertypes: [],
  types: [],
  subtypes: [],
  ja_type_line: 'q',
  ja_supertypes: [],
  ja_types: [],
  ja_subtypes: [],
  oracle_text: 'q',
  ja_text: 'q',
  power: 'q',
  toughness: 'q',
  colors: [],
  color_identity: [],
  layout: 'q',
  card_faces: undefined,
  printings: [],
  first_printing: 'q',
  first_modern_printing: 'q',
  modern_legal: 'q',
  image_url: 'q',
  image_url_small: 'q',
  image_url_large: 'q'
};

const defaultCardFace: CardFace = {
  original_name: 'q',
  ja_name: 'q',
  name_furigana: 'q',
  mana_cost: 'q',
  type_line: 'q',
  ja_type_line: 'q',
  oracle_text: 'q',
  ja_text: 'q',
};
